/**
 * ChatPanel Component
 *
 * @module core/chat/components
 * @version 1.0
 *
 * @description
 * Panel flotante con glassmorphism para el chat IA.
 * Se posiciona en el lado derecho de la pantalla, superpuesto al mapa.
 */

import { useEffect, useRef, useState } from 'react';
import {
  X,
  Send,
  Trash2,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  ShieldAlert,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useChat } from '../hooks/useChat';
import { useProject } from '../../projects';
import type { ChatContextSummary, ChatMessage } from '../types';
import './chat-panel.css';
import { WaylineChatIcon } from './WaylineChatIcon';

export interface ChatPanelProps {
  /**
   * Si el panel está abierto
   */
  isOpen: boolean;

  /**
   * Callback cuando se cierra el panel
   */
  onClose: () => void;
}

/**
 * Panel flotante del chat IA
 *
 * @remarks
 * - Posición fixed en lado derecho
 * - Glassmorphism intenso
 * - Scroll automático a último mensaje
 * - Input con auto-focus
 * - Soporte para Ctrl+Enter para enviar
 */
export function ChatPanel({ isOpen, onClose }: ChatPanelProps): JSX.Element | null {
  const {
    messages,
    error,
    isLoading,
    hasOlderMessages,
    isLoadingOlder,
    sendMessage,
    loadOlderMessages,
    clearMessages,
    clearError,
  } = useChat();
  const { projectId, availableProjects } = useProject();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const skipAutoScrollRef = useRef(false);
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const messageBodyRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const loadingRef = useRef<HTMLDivElement>(null);
  const previousLastMessageIdRef = useRef<string | null>(null);
  const previousIsLoadingRef = useRef(false);
  const hasInitializedScrollRef = useRef(false);

  const [showContextDetails, setShowContextDetails] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  /**
   * Política de scroll:
   * - envío / "Pensando…" => fondo del chat
   * - respuesta assistant nueva => inicio de ese mensaje
   * - carga de historial => preservar posición manualmente
   */
  useEffect(() => {
    const container = messagesRef.current;
    const lastMessage = messages[messages.length - 1];
    const previousLastMessageId = previousLastMessageIdRef.current;
    const previousIsLoading = previousIsLoadingRef.current;

    if (!container) return;

    if (skipAutoScrollRef.current) {
      skipAutoScrollRef.current = false;
      previousLastMessageIdRef.current = lastMessage?.id ?? null;
      previousIsLoadingRef.current = isLoading;
      return;
    }

    if (!hasInitializedScrollRef.current) {
      hasInitializedScrollRef.current = true;
      previousLastMessageIdRef.current = lastMessage?.id ?? null;
      previousIsLoadingRef.current = isLoading;
      return;
    }

    if (isLoading && !previousIsLoading) {
      const loadingElement = loadingRef.current;
      const anchorMessage = lastMessage ? messageRefs.current[lastMessage.id] : null;
      const scrollTarget = loadingElement ?? anchorMessage;

      if (scrollTarget) {
        scrollTarget.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      } else {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        });
      }
    } else if (
      !isLoading &&
      previousIsLoading &&
      lastMessage &&
      lastMessage.role === 'assistant' &&
      lastMessage.id !== previousLastMessageId
    ) {
      const lastMessageElement = messageRefs.current[lastMessage.id];
      if (lastMessageElement) {
        lastMessageElement.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }
    }

    previousLastMessageIdRef.current = lastMessage?.id ?? null;
    previousIsLoadingRef.current = isLoading;
  }, [messages, isLoading]);

  useEffect(() => {
    if (!copyFeedback) return;

    const timeout = window.setTimeout(() => setCopyFeedback(false), 1800);
    return () => window.clearTimeout(timeout);
  }, [copyFeedback]);

  useEffect(() => {
    if (!copiedMessageId) return;

    const timeout = window.setTimeout(() => setCopiedMessageId(null), 1800);
    return () => window.clearTimeout(timeout);
  }, [copiedMessageId]);

  /**
   * Auto-focus en input cuando se abre
   */
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  /**
   * Maneja el envío del mensaje
   */
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!inputRef.current) return;

    const content = inputRef.current.value.trim();
    if (!content || isLoading) return;

    // Enviar mensaje
    await sendMessage(content);

    // Limpiar input
    inputRef.current.value = '';
    inputRef.current.style.height = 'auto';
  };

  /**
   * Maneja Enter para enviar, Shift+Enter para salto de línea
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const form = e.currentTarget.form;
      if (form) {
        form.requestSubmit();
      }
    }
  };

  /**
   * Auto-resize del textarea
   */
  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const target = e.currentTarget;
    target.style.height = 'auto';
    target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
  };

  /**
   * Formatea el timestamp del mensaje
   */
  const formatTimestamp = (date: Date): string => {
    return new Intl.DateTimeFormat('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const latestContextSummary: ChatContextSummary | null =
    [...messages].reverse().find((message) => message.contextSummary)?.contextSummary || null;
  const activeProject = availableProjects.find((project) => project.id === projectId);

  const getCompletenessLabel = (
    completeness: ChatContextSummary['dataCompleteness'],
  ): string => {
    switch (completeness) {
      case 'sufficient':
        return 'Contexto suficiente';
      case 'partial':
        return 'Contexto parcial';
      default:
        return 'Sin contexto útil';
    }
  };

  const getScopeLabel = (scope: ChatContextSummary['scope']): string => {
    switch (scope) {
      case 'selection':
        return 'Selección';
      case 'module':
        return 'Módulo';
      case 'current-view':
        return 'Vista actual';
      default:
        return 'Proyecto';
    }
  };

  const getCompactCompletenessLabel = (
    completeness: ChatContextSummary['dataCompleteness'],
  ): string => {
    switch (completeness) {
      case 'sufficient':
        return 'Contexto suficiente';
      case 'partial':
        return 'Contexto parcial';
      default:
        return 'Sin contexto';
    }
  };

  /**
   * Renderiza un mensaje individual
   */
  const renderMessage = (message: ChatMessage) => {
    const isUser = message.role === 'user';
    const isAssistant = message.role === 'assistant';

    return (
      <div
        key={message.id}
        className={`chat-message ${isUser ? 'user' : 'assistant'}`}
        ref={(element) => {
          messageRefs.current[message.id] = element;
        }}
      >
        <div className="message-content">
          <div
            className="message-text"
            ref={(element) => {
              messageBodyRefs.current[message.id] = element;
            }}
          >
            {isUser ? (
              message.content
            ) : (
              <ReactMarkdown>{message.content}</ReactMarkdown>
            )}
          </div>
          <div className="message-meta">
            <div className="message-timestamp">{formatTimestamp(message.timestamp)}</div>
            {isAssistant && (
              <button
                type="button"
                className={`message-copy-btn ${copiedMessageId === message.id ? 'copied' : ''}`}
                onClick={() => {
                  void handleCopyMessage(message);
                }}
                aria-label={copiedMessageId === message.id ? 'Respuesta copiada' : 'Copiar respuesta'}
                title={copiedMessageId === message.id ? 'Copiado' : 'Copiar respuesta'}
              >
                {copiedMessageId === message.id ? <Check size={14} /> : <Copy size={14} />}
                <span>{copiedMessageId === message.id ? 'Copiado' : 'Copiar'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleLoadOlderMessages = async () => {
    if (!messagesRef.current) {
      await loadOlderMessages();
      return;
    }

    const container = messagesRef.current;
    const previousScrollHeight = container.scrollHeight;
    const previousScrollTop = container.scrollTop;
    const loadedCount = await loadOlderMessages();

    if (loadedCount > 0) {
      skipAutoScrollRef.current = true;
      window.requestAnimationFrame(() => {
        const nextScrollHeight = container.scrollHeight;
        container.scrollTop = nextScrollHeight - previousScrollHeight + previousScrollTop;
      });
    }
  };

  const handleCopyChat = async () => {
    const copiedAt = new Intl.DateTimeFormat('es-AR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date());

    const lines = [
      `Proyecto: ${activeProject?.name || 'Proyecto activo'}`,
      `Conversación: ${messages[messages.length - 1]?.conversationId || 'sin conversación activa'}`,
      `Copiado: ${copiedAt}`,
      'Nota: Se copiaron los mensajes cargados actualmente.',
      '',
    ];

    messages.forEach((message) => {
      const roleLabel = message.role === 'user' ? 'Usuario' : 'Asistente';
      lines.push(`[${formatTimestamp(message.timestamp)}] ${roleLabel}:`);
      lines.push(message.content);
      lines.push('');
    });

    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopyFeedback(true);
    } catch (err) {
      console.error('[ChatPanel] Error al copiar chat:', err);
    }
  };

  const handleCopyMessage = async (message: ChatMessage) => {
    try {
      const visibleText =
        messageBodyRefs.current[message.id]?.innerText.trim() || message.content;
      await navigator.clipboard.writeText(visibleText);
      setCopiedMessageId(message.id);
    } catch (err) {
      console.error('[ChatPanel] Error al copiar respuesta:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <aside className="chat-panel" role="complementary" aria-label="Chat con IA">
        {/* Header */}
        <header className="chat-header">
          <div className="chat-header-title">
            <WaylineChatIcon size={20} className="chat-icon" />
            <h2>Asistente IA</h2>
          </div>

          <div className="chat-header-actions">
            {messages.length > 0 && (
              <button
                type="button"
                className="chat-header-btn"
                onClick={() => {
                  void handleCopyChat();
                }}
                aria-label={copyFeedback ? 'Chat copiado' : 'Copiar chat'}
                title={copyFeedback ? 'Chat copiado' : 'Copiar chat'}
              >
                {copyFeedback ? <Check size={18} /> : <Copy size={18} />}
              </button>
            )}
            {messages.length > 0 && (
              <button
                type="button"
                className="chat-header-btn"
                onClick={() => {
                  void clearMessages();
                }}
                aria-label="Limpiar conversación"
                title="Limpiar conversación"
              >
                <Trash2 size={18} />
              </button>
            )}

            <button
              type="button"
              className="chat-header-btn"
              onClick={onClose}
              aria-label="Cerrar chat"
              title="Cerrar chat"
            >
              <X size={20} />
            </button>
          </div>
        </header>

        {latestContextSummary && (
          <section className="chat-context-summary" aria-label="Contexto usado por el copiloto">
            <button
              type="button"
              className="chat-context-summary__toggle"
              onClick={() => setShowContextDetails((value) => !value)}
              aria-expanded={showContextDetails}
            >
              <div className="chat-context-summary__heading compact">
                <ShieldAlert size={14} />
                <div className="chat-context-summary__title-row">
                  <span className={`context-badge ${latestContextSummary.dataCompleteness}`}>
                    {getCompactCompletenessLabel(latestContextSummary.dataCompleteness)}
                  </span>
                  <span className="chat-context-summary__inline-stat">
                    Obs. {latestContextSummary.countsIncluded}
                  </span>
                  <span className="chat-context-summary__inline-stat">
                    Calc. {latestContextSummary.metricsIncluded}
                  </span>
                  <span className="chat-context-summary__inline-stat">
                    Fuentes {latestContextSummary.sources.length}
                  </span>
                </div>
              </div>

              <div className="chat-context-summary__badges compact">
                <span className="context-badge scope">
                  {getScopeLabel(latestContextSummary.scope)}
                </span>
                {showContextDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </div>
            </button>

            {showContextDetails && (
              <div className="chat-context-summary__details">
                <div className="context-detail-block">
                  <div className="context-detail-label">Resumen</div>
                  <div className="context-detail-value">
                    {getCompletenessLabel(latestContextSummary.dataCompleteness)} · Scope {getScopeLabel(latestContextSummary.scope)} · Obs. {latestContextSummary.countsIncluded} · Calc. {latestContextSummary.metricsIncluded} · Fuentes {latestContextSummary.sources.length}
                  </div>
                </div>

                <div className="context-detail-block">
                  <div className="context-detail-label">Observado</div>
                  <div className="context-detail-value">
                    {latestContextSummary.countsIncluded} conteos agregados disponibles
                  </div>
                </div>

                <div className="context-detail-block">
                  <div className="context-detail-label">Calculado</div>
                  <div className="context-detail-value">
                    {latestContextSummary.metricsIncluded} métricas agregadas disponibles
                  </div>
                </div>

                <div className="context-detail-block">
                  <div className="context-detail-label">Fuentes</div>
                  <div className="context-source-list">
                    {latestContextSummary.sources.map((source) => (
                      <span key={source.key} className="context-source-pill">
                        {source.label}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="context-detail-block">
                  <div className="context-detail-label">Límites</div>
                  <div className="context-limits">
                    <span className="context-limit-pill">
                      Modificación: {latestContextSummary.limits.canModifyData ? 'sí' : 'no'}
                    </span>
                    <span className="context-limit-pill">
                      Informe final: {latestContextSummary.limits.canExportFinalReports ? 'sí' : 'no'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Messages Area */}
        <div className="chat-messages" ref={messagesRef}>
          {hasOlderMessages && (
            <button
              type="button"
              className="chat-load-older-btn"
              onClick={() => {
                void handleLoadOlderMessages();
              }}
              disabled={isLoadingOlder}
            >
              {isLoadingOlder ? 'Cargando…' : 'Cargar mensajes anteriores'}
            </button>
          )}

          {messages.length === 0 ? (
            <div className="chat-empty-state">
              <WaylineChatIcon size={48} className="empty-icon" />
              <h3>Copiloto Interno</h3>
              <p>Asiste análisis y validación dentro de Wayline</p>

              {/* Ejemplos de preguntas */}
              <div className="example-questions">
                <button
                  type="button"
                  className="example-btn"
                  onClick={() => {
                    if (inputRef.current) {
                      inputRef.current.value = '¿Cuántos árboles hay?';
                      inputRef.current.focus();
                    }
                  }}
                >
                  ¿Cuántos árboles hay?
                </button>
                <button
                  type="button"
                  className="example-btn"
                  onClick={() => {
                    if (inputRef.current) {
                      inputRef.current.value = '¿Dónde está la calculadora?';
                      inputRef.current.focus();
                    }
                  }}
                >
                  ¿Dónde está la calculadora?
                </button>
                <button
                  type="button"
                  className="example-btn"
                  onClick={() => {
                    if (inputRef.current) {
                      inputRef.current.value = 'Analiza la movilidad';
                      inputRef.current.focus();
                    }
                  }}
                >
                  Analiza la movilidad
                </button>
              </div>
            </div>
          ) : (
            <>
              {messages.map(renderMessage)}

              {/* Loading indicator */}
              {isLoading && (
                <div
                  className="chat-message assistant loading"
                  ref={loadingRef}
                >
                  <div className="message-content">
                    <div className="message-text typing-bubble">
                      <span className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </span>
                      <span className="typing-label">Pensando…</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <div className="chat-error">
            <span>⚠️ {error}</span>
            <button
              type="button"
              onClick={clearError}
              aria-label="Cerrar error"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Input Area */}
        <form className="chat-input-form" onSubmit={handleSubmit}>
          <textarea
            ref={inputRef}
            className="chat-input"
            placeholder="Escribe una consulta interna sobre el proyecto..."
            rows={1}
            disabled={isLoading}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            aria-label="Mensaje para el asistente IA"
          />

          <button
            type="submit"
            className="chat-send-btn"
            disabled={isLoading}
            aria-label="Enviar mensaje"
            title="Enviar mensaje"
          >
            <Send size={20} />
          </button>
        </form>
      </aside>
  );
}
